import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

import { dockReducer, initialDockState, type DockState } from './dock-reducer';

export interface MessagingDockContextValue {
  readonly state: DockState;
  /** Bubble click: collapsed → last open view, open → collapsed. */
  readonly toggle: () => void;
  /** Open a conversation thread inside the panel. */
  readonly openThread: (conversationId: string) => void;
  /** Thread → list. */
  readonly back: () => void;
  /** Explicit collapse to the bubble (the panel's minimize/close control). */
  readonly close: () => void;
  /** Unsent draft text for a conversation, restored when its thread remounts. */
  readonly getDraft: (conversationId: string) => string;
  /** Persist (or clear, on '') a conversation's unsent draft. */
  readonly setDraft: (conversationId: string, text: string) => void;
}

const MessagingDockContext = createContext<MessagingDockContextValue | null>(null);

// Mounted above BOTH the `_authed` and `_public` layout routes (in main.tsx)
// so the dock's open/collapsed state survives every navigation — including the
// `_authed ↔ _public` boundary, which remounts the Shell. The dock VIEW lives
// in Shell; this provider holds only the UI state, so it needs no viewer or
// query context of its own.
export function MessagingDockProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(dockReducer, initialDockState);

  // Unsent drafts, keyed by conversationId. A ref (not state) on purpose: this
  // provider wraps the router, so keystroke-frequency setState here would
  // re-render the whole app. The composer only reads a draft on mount
  // (`initialDraft`) and writes on change, so a mutable ref is sufficient and
  // survives the composer's unmount (minimize/close) and app navigation.
  const draftsRef = useRef<Record<string, string>>({});
  const getDraft = useCallback(
    (conversationId: string) => draftsRef.current[conversationId] ?? '',
    [],
  );
  const setDraft = useCallback((conversationId: string, text: string) => {
    if (text.length === 0) {
      delete draftsRef.current[conversationId];
    } else {
      draftsRef.current[conversationId] = text;
    }
  }, []);

  const value = useMemo<MessagingDockContextValue>(
    () => ({
      state,
      toggle: () => dispatch({ type: 'toggle' }),
      openThread: (conversationId: string) =>
        dispatch({ type: 'openThread', conversationId }),
      back: () => dispatch({ type: 'back' }),
      close: () => dispatch({ type: 'close' }),
      getDraft,
      setDraft,
    }),
    [state, getDraft, setDraft],
  );

  return (
    <MessagingDockContext.Provider value={value}>
      {children}
    </MessagingDockContext.Provider>
  );
}

export function useMessagingDock(): MessagingDockContextValue {
  const ctx = useContext(MessagingDockContext);
  if (ctx === null) {
    throw new Error(
      'useMessagingDock must be used within a <MessagingDockProvider>. The provider is mounted above both layout routes in main.tsx.',
    );
  }
  return ctx;
}
